import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { Extras } from '@sentry/types';
import Mixpanel from 'mixpanel-browser';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Metric } from 'web-vitals';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { errorReports, telemetryInit } from './shared/telemetry/telemetry';
import { version } from '@/../package.json';

interface WebVitalsMetrics extends Extras {
  metric: Metric;
}

const {
  REACT_APP_ENVIRONMENT = 'development',
  REACT_APP_DATADOG_APPLICATION_ID,
  REACT_APP_DATADOG_CLIENT_TOKEN,
  NODE_ENV,
} = process.env;

telemetryInit();

Mixpanel.init('77ea83dadd28d26ba8d46954f408fb2b', {
  debug: REACT_APP_ENVIRONMENT !== 'production',
});

Mixpanel.register_once({
  ENVIRONMENT: REACT_APP_ENVIRONMENT.toUpperCase(),
});

let datadogEnv = 'local';
switch (REACT_APP_ENVIRONMENT) {
  case 'production':
    datadogEnv = 'prod';
    break;
  case 'staging':
    datadogEnv = 'stg';
    break;
  case 'qa':
    datadogEnv = 'qa';
    break;
}

datadogRum.init({
  applicationId: String(REACT_APP_DATADOG_APPLICATION_ID),
  clientToken: String(REACT_APP_DATADOG_CLIENT_TOKEN),
  // `site` refers to the Datadog site parameter of your organization
  // see https://docs.datadoghq.com/getting_started/site/
  site: 'datadoghq.com',
  service: 'sov-v2-app-companies-browser',
  env: datadogEnv,
  version,
  // Specify a version number to identify the deployed version of your application in Datadog
  sessionSampleRate: 70,
  sessionReplaySampleRate: 70,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  traceContextInjection: 'all',
  defaultPrivacyLevel: 'allow',
  allowedTracingUrls: [/empresas\.vitta\.com\.br/],
});

datadogLogs.init({
  clientToken: String(REACT_APP_DATADOG_CLIENT_TOKEN),
  site: 'datadoghq.com',
  forwardErrorsToLogs: true,
  sessionSampleRate: 100,
  service: 'sov-v2-app-companies-browser',
  env: NODE_ENV,
});

// Monitoramento dos Web Vitals
reportWebVitals((report: Metric) => {
  if (REACT_APP_ENVIRONMENT === 'production') {
    if (report.name) {
      errorReports.category({
        name: 'Web Vitals',
        description: report.name,
        level: 'info',
      });
    }

    const extrasConvert: WebVitalsMetrics = {
      metric: report,
    };

    errorReports.event({
      name: 'Web Vitals',
      content: extrasConvert,
      level: 'info',
    });
  }
});

const container = document.getElementById('root');
if (container) {
  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
} else {
  console.error('Element with id #root not found');
}
